/**
 * Team CRUD, membership, and bulk operations
 */

import type { Team } from '../../graphql/types';
import { apiRequest } from './http';
import { createEntityCrudClient, createBulkOperationsClient } from './crudFactory';
import type { BulkOperationResult } from './crudFactory';

export interface CreateTeamInput {
  name: string;
  description?: string;
  type: string;
  parentTeamId?: string;
  leadUserId: string;
  memberIds: string[];
  responsibilities: string[];
  contactEmail: string;
  teamsChannel?: string;
}

export interface UpdateTeamInput {
  name?: string;
  description?: string;
  type?: string;
  parentTeamId?: string;
  leadUserId?: string;
  memberIds?: string[];
  responsibilities?: string[];
  contactEmail?: string;
  teamsChannel?: string;
}

const client = createEntityCrudClient<Team, CreateTeamInput, UpdateTeamInput>('/teams');

/**
 * Create a new team
 */
export async function createTeam(input: CreateTeamInput): Promise<Team> {
  return client.create(input);
}

/**
 * Update an existing team
 */
export async function updateTeam(teamId: string, input: UpdateTeamInput): Promise<Team> {
  return client.update(teamId, input);
}

/**
 * Delete a team
 */
export async function deleteTeam(teamId: string, hardDelete: boolean = false): Promise<void> {
  return client.remove(teamId, hardDelete);
}

/**
 * Add a member to a team
 */
export async function addTeamMember(teamId: string, userId: string): Promise<Team> {
  return apiRequest<Team>(`/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

/**
 * Remove a member from a team
 */
export async function removeTeamMember(teamId: string, userId: string): Promise<Team> {
  return apiRequest<Team>(`/teams/${teamId}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

const bulkClient = createBulkOperationsClient<Pick<UpdateTeamInput, 'type'>>('/teams');

/**
 * Bulk delete teams
 */
export async function bulkDeleteTeams(
  ids: string[],
  hardDelete = false
): Promise<BulkOperationResult> {
  return bulkClient.bulkDelete(ids, hardDelete);
}

/**
 * Bulk update common fields on multiple teams
 */
export async function bulkUpdateTeams(
  ids: string[],
  updates: Pick<UpdateTeamInput, 'type'>
): Promise<BulkOperationResult> {
  return bulkClient.bulkUpdate(ids, updates);
}
